import datadogLogs from 'logger'
import { GraphQLClient } from 'graphql-request'
import jwt_decode from 'jwt-decode'
import dataProvider from './DataProvider'
import { getCsrfToken } from 'utils'

export const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT ?? ''
export const GRAPHQL_ENDPOINT = API_ENDPOINT + 'graphql'
export const AUTH_API_BASE = API_ENDPOINT

export type ApiManagementUser = {
  id: string
  email: string
  user_name: string
  is_active: boolean
  department: string | null
  label: string | null
  authorities: string[]
}

export type LoginResponse = {
  access_token: string | null
  token_type: string | null
  requires_otp: boolean
  message: string | null
  user: ApiManagementUser
}

export type RefreshResponse = {
  access_token: string
  token_type: string
}

export const isJwtExpired = (token: string) => {
  if (typeof token !== 'string' || !token) throw new Error('Invalid token provided')

  let isJwtExpired = false
  const decoded: any = jwt_decode(token)
  if (!('exp' in decoded)) {
    return isJwtExpired
  }
  const currentTime = new Date().getTime() / 1000

  if (currentTime > decoded.exp) isJwtExpired = true

  return isJwtExpired
}

const refreshAccessToken = async (email?: string) => {
  const csrfToken = getCsrfToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(`${AUTH_API_BASE}api/auth/management-user/refresh-token`, {
    method: 'POST',
    headers,
    credentials: 'include',
  })

  if (!response.ok) {
    datadogLogs.logger.error('[refresh token] failed', { status: response.status })
    throw Error('アクセストークンの更新に失敗しました')
  }

  const data: RefreshResponse = await response.json()
  const newToken = data.access_token
  if (!newToken) throw Error('Access token is not available')

  localStorage.setItem('accessToken', newToken)

  if (email) {
    datadogLogs.logger.info(`[Access token has updated] ${email}`)
  }

  return newToken
}

export const getClient = async () => {
  let accessToken = localStorage.getItem('accessToken')
  if (!accessToken) return Promise.reject()

  let currentUser = null
  try {
    const user = localStorage.getItem('currentUser')
    if (user) currentUser = JSON.parse(user)
  } catch (error) {
    datadogLogs.logger.warn('[getClient] Failed to parse currentUser')
  }

  // アクセストークンを検証
  let expired = false
  try {
    expired = isJwtExpired(accessToken)
  } catch {
    return Promise.reject()
  }

  // GraphQLクライアントのオプションを構築（CSRF対策付き）
  const buildClientOptions = (token: string) => {
    const csrfToken = getCsrfToken()
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    }
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken
    }
    return {
      headers,
      credentials: 'include' as RequestCredentials,
    }
  }

  // 有効な場合はヘッダに付与して返却
  if (!expired) return new GraphQLClient(GRAPHQL_ENDPOINT, buildClientOptions(accessToken))

  // 期限切れの場合はトークンをリフレッシュする
  try {
    const newToken = await refreshAccessToken(currentUser?.email)
    return new GraphQLClient(GRAPHQL_ENDPOINT, buildClientOptions(newToken))
  } catch (e) {
    if (currentUser?.email) {
      datadogLogs.logger.error(`[Failed to refresh access token] loginID: ${currentUser.email}`)
    }
    localStorage.setItem('authError', '再ログインしてください')
    return Promise.reject()
  }
}

export const gqlDataProvider = dataProvider()
